import { useState } from 'react';
import { useIntl } from 'react-intl';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { Tile, TileSkeleton } from '@rossum/components/tile';
import { TileChip } from '@rossum/components/tile/types';
import { EmptyStateRadar } from '@rossum/ui';
import {
  IconFolder,
  IconListCheck,
  IconSitemap,
} from '@rossum/ui/icons/tabler';
import { Alert, Stack, Typography } from '@rossum/ui/material';
import { HelmetComponent } from '../../../routes/HelmetComponent';
import {
  useWorkflowsWithSteps,
  WorkflowWithSteps,
} from './hooks/useWorkflowsWithSteps';
import { WorkflowsSettingsHeader } from './WorkflowsSettingsHeader';

const WorkflowTile = ({ workflow }: { workflow: WorkflowWithSteps }) => {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(false);

  const stepsChip: TileChip = {
    key: 'steps-count',
    label: intl.formatMessage(
      { id: 'features.workflows.settings.stepsCount' },
      { count: workflow.steps.length }
    ),
  };

  const queueChips: TileChip[] = workflow.queues.map(queue => ({
    key: `queue-${queue.id}`,
    label: queue.name,
    Icon: IconFolder,
  }));

  return (
    <Tile
      data-cy={`workflow-tile-${workflow.id}`}
      iconProps={{ content: IconSitemap, size: 'small' }}
      title={workflow.name}
      chips={queueChips.length > 0 ? queueChips : undefined}
      endChips={[stepsChip]}
      dragHandle={null}
      expandable={{
        expanded,
        onToggleExpanded: () => setExpanded(prev => !prev),
        content: (
          <Stack gap={1} p={2}>
            {workflow.steps.length > 0 ? (
              workflow.steps.map((step, index) => (
                <Tile
                  key={step.id}
                  data-cy={`workflow-step-tile-${step.id}`}
                  iconProps={{ content: IconListCheck, size: 'small' }}
                  title={`${index + 1}. ${step.name}`}
                  dragHandle={null}
                  endChips={[
                    {
                      key: 'mode',
                      label: intl.formatMessage({
                        id: `features.workflows.settings.stepMode.${step.mode}`,
                      }),
                    },
                  ]}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" px={1}>
                {intl.formatMessage({
                  id: 'features.workflows.settings.noSteps',
                })}
              </Typography>
            )}
          </Stack>
        ),
      }}
    />
  );
};

export const WorkflowsSettingsPage = () => {
  const intl = useIntl();
  const { data: workflows, isLoading, isError } = useWorkflowsWithSteps();

  return (
    <>
      <HelmetComponent translationKey="features.workflows.settings.header.title" />
      <PageLayout
        renderHeader={params => <WorkflowsSettingsHeader {...params} />}
      >
        <Stack px={4} py={4} gap={2}>
          <Alert severity="info">
            {intl.formatMessage({
              id: 'features.workflows.settings.readOnlyNote',
            })}
          </Alert>
          {isLoading ? (
            Array.from({ length: 6 }, (_, i) => (
              <TileSkeleton key={i} skeletons={['icon', 'title', 'endChips']} />
            ))
          ) : isError ? (
            <EmptyStateRadar
              title={intl.formatMessage({
                id: 'features.workflows.settings.error.title',
              })}
              description={intl.formatMessage({
                id: 'features.workflows.settings.error.description',
              })}
            />
          ) : workflows && workflows.length > 0 ? (
            workflows.map(workflow => (
              <WorkflowTile key={workflow.id} workflow={workflow} />
            ))
          ) : (
            <EmptyStateRadar
              iconProps={{ Icon: IconSitemap, iconSize: 36 }}
              title={intl.formatMessage({
                id: 'features.workflows.settings.empty.title',
              })}
              description={intl.formatMessage({
                id: 'features.workflows.settings.empty.description',
              })}
            />
          )}
        </Stack>
      </PageLayout>
    </>
  );
};
